/**
 * People list and search (`/people`) — docs/08-frontend-and-ux.md §3.13.
 *
 * The search box is the diacritic-insensitive trigram search of docs/05 §5.2, so
 * "Fes" finds "Fès"; the query lives in the URL so a result set can be shared and
 * the back button behaves.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { PersonSummary } from '@famtree/shared';
import { personsApi } from '../../api/client.js';
import { useSession } from '../../app/session.js';
import { problemMessage } from '../../ui/problem.js';
import { FuzzyDateText } from '../../ui/FuzzyDateField.js';
import { lifespan, personName } from './labels.js';
import { LineageSearch } from './LineageSearch.js';
import ui from '../../ui/components.module.css';
import styles from './people.module.css';

const SORTS = ['family_name', 'birth', '-birth', '-created_at'] as const;

export function PeopleListPage(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useSession();
  const [params, setParams] = useSearchParams();

  const q = params.get('q') ?? '';
  const sort = params.get('sort') ?? '';
  const [draft, setDraft] = useState(q);

  // Debounced: the search index is fast, but one request per keystroke is rude.
  useEffect(() => {
    if (draft === q) return;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (draft) next.set('q', draft);
      else next.delete('q');
      setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(timer);
  }, [draft, q, params, setParams]);

  const people = useQuery({
    queryKey: ['persons', { q, sort }],
    queryFn: () => personsApi.list({ q, sort, limit: 50 }),
    placeholderData: keepPreviousData,
  });

  const canWrite = user?.role === 'admin' || user?.can_propose;

  return (
    <div>
      <LineageSearch />

      <div className={styles.header}>
        <h1>{t('people.title')}</h1>
        <span className={styles.spacer} />
        {canWrite && (
          <Link className={ui.button} to="/people/new">
            {t('people.addPerson')}
          </Link>
        )}
      </div>

      <div className={styles.searchRow}>
        <label className={`${ui.field} ${styles.grow}`} style={{ marginBlockEnd: 0 }}>
          <span className={ui.label}>{t('people.search')}</span>
          <input
            className={ui.input}
            type="search"
            value={draft}
            placeholder={t('people.searchPlaceholder')}
            onChange={(event) => setDraft(event.target.value)}
          />
        </label>

        <label className={ui.field} style={{ marginBlockEnd: 0 }}>
          <span className={ui.label}>{t('people.sort')}</span>
          <select
            className={ui.input}
            value={sort}
            onChange={(event) => {
              const next = new URLSearchParams(params);
              if (event.target.value) next.set('sort', event.target.value);
              else next.delete('sort');
              setParams(next, { replace: true });
            }}
          >
            <option value="">{t('people.sorts.default')}</option>
            {SORTS.map((option) => (
              <option key={option} value={option}>
                {t(`people.sorts.${option}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {people.isError && <p className={ui.problem}>{problemMessage(people.error, t)}</p>}
      {people.isPending && <p className={ui.muted}>{t('app.loading')}</p>}

      {people.data && people.data.data.length === 0 && (
        <p className={ui.muted}>{q ? t('people.noMatches', { q }) : t('people.empty')}</p>
      )}

      {people.data && people.data.data.length > 0 && (
        <>
          <p className={ui.muted}>
            {t('people.count', { count: people.data.page.total ?? people.data.data.length })}
          </p>
          <ul className={styles.list}>
            {people.data.data.map((person) => (
              <PersonRow key={person.id} person={person} />
            ))}
          </ul>
          {people.data.page.has_more && <p className={ui.muted}>{t('people.moreAvailable')}</p>}
        </>
      )}
    </div>
  );
}

function PersonRow({ person }: { person: PersonSummary }): JSX.Element {
  const { t } = useTranslation();
  const span = lifespan(person);

  return (
    <li>
      <Link className={styles.row} to={`/people/${person.id}`}>
        <span className={styles.rowName}>{personName(person)}</span>
        {person.nickname && <span className={ui.muted}>“{person.nickname}”</span>}
        <span className={`${ui.muted} tabular`}>
          {span ?? <FuzzyDateText value={person.birth} />}
        </span>
        <span className={styles.spacer} />
        {person.status === 'pending' && (
          <span className={styles.badgePending}>{t('enums.assertion_status.pending')}</span>
        )}
        <span className={styles.badge}>{t(`enums.living_status.${person.living_status}`)}</span>
      </Link>
    </li>
  );
}
